import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useWebAndExtRouterControl} from 'src/components/micro-apps-extension/hooks/useWebAndExtRouterControl';
import {useHistory} from 'src/rerouter';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment-timezone';

import {
  Toast,
  ToastBody,
  ToastFooter,
  ToastTitle,
  toastApi,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';

import {
  getHVBroadcastData,
  fetchDripBroadcastDetails,
} from 'src/action-creators/hv-broadcasts';
import {getTimezone} from 'src/selectors/date-time';


type ReturnValue = {
  isHVBroadcastLocation: boolean;
  isScheduledHVBroadcastLocation: boolean;
  showBroadcastRenamedSuccessfully: (
    id: string,
    prevName: string,
    currName: string,
    isDripBroadcast?: boolean,
  ) => void;
  showHVBroadcastSuccessfullySent: (
    id: string,
    name: string,
    isDripBroadcast?: boolean,
  ) => void;
  showHVBroadcastScheduled: (
    id: string,
    inboxId: string,
    name: string,
    date: string,
    isDripBroadcast?: boolean,
  ) => void;
  showHVBroadcastSuccessfullySaved: (id: string, name: string) => void;
  showContactListSuccessToast: (actionText: string) => void;
};

export function useHVBroadcasts(): ReturnValue {
  const {t} = useLabelTranslation();
  const router = useHistory();
  const {changeLocation} = useWebAndExtRouterControl();
  const dispatch = useDispatch();
  const location = router.location;
  const timezone = useSelector(getTimezone);
  const isHVBroadcastLocation = location.pathname.startsWith(
    '/messages/hv-broadcasts',
  );
  const isScheduledHVBroadcastLocation = location.pathname.startsWith(
    '/messages/hv-broadcasts/scheduled',
  );

  const showBroadcastRenamedSuccessfully = (
    id: string,
    prevName: string,
    currName: string,
    isDripBroadcast?: boolean,
  ) => {
    const onViewDetails = () => {
      const pathName = isDripBroadcast
        ? `/messages/hv-broadcasts/details/${id}/drip`
        : `/messages/hv-broadcasts/details/${id}`;
      changeLocation(router, pathName);
    };
    const onDismiss = (toastId: string) => {
      toastApi.remove(toastId);
    };
    const toastId = toastApi.show(
      <Toast initialFocus={0} semantic="success">
        <ToastTitle>{`${t(
          'Broadcast_Named_Successfully',
          'Broadcast Named Successfully!',
        )}`}</ToastTitle>
        <ToastBody>{`'${prevName}' ${t(
          'renamed_to',
          'renamed to',
        )} '${currName}'`}</ToastBody>
        <ToastFooter>
          <Button onClick={() => onDismiss(toastId)}>{`${t(
            'Dismiss',
            'Dismiss',
          )}`}</Button>
          <Button onClick={onViewDetails}>{`${t(
            'View_Details',
            'View Details',
          )}`}</Button>
        </ToastFooter>
      </Toast>,
    );
  };

  const showHVBroadcastSuccessfullySent = (
    id: string,
    name: string,
    isDripBroadcast: boolean = false,
  ) => {
    const onViewDetails = () => {
      const pathName = isDripBroadcast
        ? `/messages/hv-broadcasts/details/${id}/drip`
        : `/messages/hv-broadcasts/details/${id}`;
      changeLocation(router, pathName);
    };
    const onDismiss = (toastId: string) => {
      toastApi.remove(toastId);
    };
    const toastId = toastApi.show(
      <Toast initialFocus={0} semantic="success">
        <ToastTitle>{`${t('Success', 'Success')}${'!'}`}</ToastTitle>
        <ToastBody>{`'${name}' ${t(
          'broadcast_sent_successfully',
          'broadcast sent successfully.',
        )}`}</ToastBody>
        <ToastFooter>
          <Button onClick={() => onDismiss(toastId)}>{`${t(
            'Dismiss',
            'Dismiss',
          )}`}</Button>
          <Button onClick={onViewDetails}>{`${t(
            'View_Details',
            'View Details',
          )}`}</Button>
        </ToastFooter>
      </Toast>,
    );
  };

  const showHVBroadcastScheduled = (
    id: string,
    inboxId: string,
    name: string,
    date: string,
    isDripBroadcast: boolean = false,
  ) => {
    const onEditDetails = async () => {
      const pathName = isDripBroadcast
        ? `/messages/hv-broadcasts/scheduled/${id}/from/${inboxId}/drip`
        : `/messages/hv-broadcasts/scheduled/${id}/from/${inboxId}`;
      if (isDripBroadcast) {
        await dispatch(fetchDripBroadcastDetails(id));
        changeLocation(router, pathName);
      } else {
        await dispatch(getHVBroadcastData(id));
        changeLocation(router, pathName);
      }
    };
    const onDismiss = (toastId: string) => {
      toastApi.remove(toastId);
    };
    const formattedDate = `${t('Scheduled_On', 'Scheduled On')}: ${moment(date)
      .tz(timezone)
      .format('MMM Do, YYYY, HH:mma')}`;
    const toastId = toastApi.show(
      <Toast initialFocus={0} semantic="success" time={formattedDate}>
        <ToastTitle>{`${t('Success', 'Success')}${'!'}`}</ToastTitle>
        <ToastBody>{`'${name}' ${t(
          'broadcast_scheduled_successfully',
          'broadcast scheduled successfully.',
        )}`}</ToastBody>
        <ToastFooter>
          <Button onClick={() => onDismiss(toastId)}>{`${t(
            'Dismiss',
            'Dismiss',
          )}`}</Button>
          <Button onClick={onEditDetails}>{`${t(
            'Edit_Details',
            'Edit Details',
          )}`}</Button>
        </ToastFooter>
      </Toast>,
    );
  };

  const showHVBroadcastSuccessfullySaved = (id: string, name: string) => {
    toastApi.show(
      <Toast initialFocus={0} semantic="success">
        <ToastTitle>{`${t('Success', 'Success')}!`}</ToastTitle>
        <ToastBody>{`'${name}' ${t(
          'broadcast_scheduled_successfully',
          'broadcast scheduled successfully.',
        )}`}</ToastBody>
      </Toast>,
    );
  };

  const showContactListSuccessToast = (actionText: string) => {
    toastApi.show(
      <Toast initialFocus={0} semantic="success">
        <ToastTitle>{`${t('Success', 'Success')}${'!'}`}</ToastTitle>
        <ToastBody>{`${`${t(
          'Contact_list',
          'Contact list',
        )}`} ${actionText} ${`${t(
          'successfully',
          'successfully.',
        )}`}`}</ToastBody>
      </Toast>,
    );
  };
  return {
    isHVBroadcastLocation,
    isScheduledHVBroadcastLocation,
    showBroadcastRenamedSuccessfully,
    showHVBroadcastSuccessfullySent,
    showHVBroadcastScheduled,
    showHVBroadcastSuccessfullySaved,
    showContactListSuccessToast,
  };
}
